import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteSpecificUserPlayers } from "../api/myClub/deleteSpecificUserPlayers";
import { fetchStreamlineSolution, resetStreamline, selectSolution, setAlternativesOpen, setCompleted, setEarnedPoints, streamlineStateSelector, swapStreamlinePlayer, toggleBracket, toggleEntry, } from "../redux/streamline/streamlineSlice";
import { asCompleted, completedEntry, completedPoints, withCompleted, } from "../streamline/completed";
import { resolveSolution } from "../streamline/swaps";
import { isBracketDone, isMarked, streamlineTotals, } from "../streamline/totals";
/**
 * Everything the Streamline solution screen needs, on both platforms: which
 * alternative is showing, what the user has marked, the numbers that follow
 * from it, and the handlers that change it.
 *
 * Web and mobile draw completely different layouts on top of this — the point
 * of the hook is that they never disagree about the state underneath.
 *
 * Marking a club card completed removes it from the imported club: the card
 * is in the SBC now, not the club. One tick removes one card, a row's Done
 * removes every club card in it, and a stack's chip removes every copy.
 * Unmarking does not bring a card back.
 */
export const useStreamlineSolution = (set, 
/** Customize, sent with every re-solve; web only. */
settings) => {
    const dispatch = useDispatch();
    const { solutionId, marks, swaps, alternativesOpen, earnedPoints, completed, options, optionsStatus, } = useSelector(streamlineStateSelector);
    const solutions = useMemo(() => set?.solutions ?? [], [set]);
    const chosen = useMemo(() => solutions.find((option) => option.id === solutionId) ??
        solutions.find((option) => option.isRecommended) ??
        solutions[0], [solutions, solutionId]);
    /* The solver's solution with any swaps folded in: what a re-solve freezes from. */
    const resolved = useMemo(() => (chosen ? resolveSolution(chosen, swaps) : undefined), [chosen, swaps]);
    /* What the screen renders: the added cards folded into their rows too. */
    const solution = useMemo(() => (resolved ? withCompleted(resolved, completed) : undefined), [resolved, completed]);
    /*
      Adopt the recommended solution on first open, and again whenever the store
      is holding an id from a set the user has since navigated away from.
    */
    useEffect(() => {
        if (chosen && chosen.id !== solutionId)
            dispatch(selectSolution(chosen.id));
    }, [dispatch, chosen, solutionId]);
    /* The next set opened should not inherit this one's marks. */
    useEffect(() => () => void dispatch(resetStreamline()), [dispatch]);
    const totals = useMemo(() => solution && set
        ? streamlineTotals(solution, set.targetPoints, marks, earnedPoints)
        : undefined, [solution, set, marks, earnedPoints]);
    /*
      What a re-solve carries across: the added cards still marked, then every
      marked card of the solver's solution, re-keyed as added cards.
    */
    const freeze = () => asCompleted([
        ...completed.filter((entry) => isMarked(marks, entry.entryId)),
        ...(resolved?.brackets ?? [])
            .flatMap((bracket) => bracket.players)
            .filter((entry) => isMarked(marks, entry.entryId)),
    ]);
    /* The club cards among `entries` leave the imported club. Fire and
       forget: the mark is the user's, whatever the request does, and nothing
       on this screen reads the club size, so the user record is left as is. */
    const removeFromClub = (entries) => {
        const ids = entries
            .map((entry) => entry.eaPlayerId)
            .filter((id) => id !== undefined);
        if (ids.length === 0)
            return;
        void deleteSpecificUserPlayers(ids).catch(() => undefined);
    };
    const entryById = (entryId) => solution?.brackets
        .flatMap((bracket) => bracket.players)
        .find((entry) => entry.entryId === entryId);
    /* Toggles each id; the ones going from open to marked leave the club. */
    const toggleEntries = (entryIds) => {
        const marking = entryIds
            .filter((entryId) => !isMarked(marks, entryId))
            .map(entryById)
            .filter((entry) => entry !== undefined);
        removeFromClub(marking);
        entryIds.forEach((entryId) => dispatch(toggleEntry(entryId)));
    };
    /** The bar's floor: the points every marked card on screen is worth. */
    const markedPoints = solution
        ? solution.brackets
            .flatMap((bracket) => bracket.players)
            .reduce((sum, entry) => isMarked(marks, entry.entryId) ? sum + entry.points : sum, 0)
        : 0;
    return {
        solution,
        solutions,
        marks,
        totals,
        alternativesOpen,
        earnedPoints,
        completed,
        /** The least the user may type as their total: the marked cards' points. */
        minPoints: markedPoints,
        options,
        optionsStatus,
        onToggleBracket: (bracket) => {
            if (!isBracketDone(bracket, marks))
                removeFromClub(bracket.players.filter((entry) => !isMarked(marks, entry.entryId)));
            dispatch(toggleBracket(bracket));
        },
        onToggleEntry: (entryId) => toggleEntries([entryId]),
        onToggleEntries: toggleEntries,
        onSwapPlayer: (entryId, option) => dispatch(swapStreamlinePlayer({ entryId, option })),
        onToggleAlternatives: () => dispatch(setAlternativesOpen(!alternativesOpen)),
        onCloseAlternatives: () => dispatch(setAlternativesOpen(false)),
        onSelectSolution: (id) => dispatch(selectSolution(id)),
        /*
          The search's cards, replaced wholesale: what the sheet holds when it
          closes. Nothing happens if the same cards come back. Otherwise the SBC
          is re-solved around them, the frozen marks still standing and every
          marked solver card, unless all of that now fills it: then there is
          nothing to solve for and the cards simply stand.
        */
        onSetAddedPlayers: (picked) => {
            if (!set)
                return;
            const added = completed.filter((entry) => entry.added);
            const same = picked.length === added.length &&
                picked.every((option) => added.some((entry) => entry.player.resourceId === option.player.resourceId &&
                    entry.eaPlayerId === option.eaPlayerId));
            if (same)
                return;
            const entries = picked.map((option, index) => completedEntry(option, option.player.itemScore ?? 0, index, true));
            /* Only the newcomers leave the club; the rest already have. */
            removeFromClub(entries.filter((entry) => !completed.some((held) => held.eaPlayerId === entry.eaPlayerId)));
            const frozen = completed.filter((entry) => !entry.added && isMarked(marks, entry.entryId));
            const solverMarked = (resolved?.brackets ?? [])
                .flatMap((bracket) => bracket.players)
                .filter((entry) => isMarked(marks, entry.entryId));
            const points = earnedPoints +
                completedPoints(entries) +
                completedPoints(frozen) +
                completedPoints(solverMarked);
            if (points >= set.targetPoints) {
                /* The solver's cards stay in their rows; only the sheet's change. */
                dispatch(setCompleted(asCompleted([
                    ...entries,
                    ...completed.filter((entry) => !entry.added),
                ])));
                return;
            }
            dispatch(fetchStreamlineSolution({
                setId: set.id,
                sbcId: set.sbcId,
                earnedPoints,
                completed: asCompleted([...entries, ...frozen, ...solverMarked]),
                settings,
            }));
        },
        /*
          A typed total. Below the marked cards' points it is raised to them; at
          or past the target the SBC is done and nothing is solved; otherwise
          the marks are frozen and the rest is solved for.
        */
        onCommitProgress: (total) => {
            if (!set)
                return;
            const clamped = Math.max(markedPoints, total);
            if (clamped >= set.targetPoints) {
                dispatch(setEarnedPoints(set.targetPoints - markedPoints));
                return;
            }
            const frozen = freeze();
            dispatch(fetchStreamlineSolution({
                setId: set.id,
                sbcId: set.sbcId,
                earnedPoints: clamped - completedPoints(frozen),
                completed: frozen,
                settings,
            }));
        },
        /* Customize's Solve again: the same progress, solved with the settings
           as they are now. */
        onSolveAgain: () => {
            if (!set)
                return;
            dispatch(fetchStreamlineSolution({
                setId: set.id,
                sbcId: set.sbcId,
                earnedPoints,
                completed: freeze(),
                settings,
            }));
        },
    };
};
//# sourceMappingURL=useStreamlineSolution.js.map